import dayjs from "dayjs";
import qs from "qs";
import DoughnutDataset from "../components/Charts/Doughnut/types/DoughnutDataset";
import ChartData from "../components/Charts/types/ChartData";
import fetchPlus from "../lib/fetchPlus/fetchPlus";
import { Action } from "../types/Action";
import { ApiResponse } from "../types/API/ApiResponse";
import { TotalPunctual } from "../types/TotalPunctual";
import { Material } from "../components/Materials/MaterialsTable/MaterialsTable";
import { ActivityDetail } from "../types/API/activity/ActivityDetail";

export interface Site {
    SiteRef: string;
    SiteName: string;
    SitePostcode: string;
    CustomerSiteNumber: string;
    ManagedRef: string;
    CustomerRegion: string;
    CustomerDivision: string;
    SiteLatitude: string;
    SiteLongitude: string;
    SiteTelephone: string;
    SiteAddress1: string;
    SiteAddress2: string;
    SiteAddress3: string;
    SiteEmail: string;
    LastRoutineVisit: string;
    LastRoutineVisitType: string;
    LastCalloutVisit: string;
    LastCalloutVisitType: string;
    LastFollowupVisit: string;
    LastFollowupVisitType: string;
    LastPestActivity: string;
    LastPestActivityType: string;
    LastMaterialUsed: string;
    LastMaterialUsedType: string;
}

// Generated by https://quicktype.io
export interface SiteVisitPreview {
    VisitID: string;
    DateConfirmed: string;
    DateCalledIn: string;
    DateServiceStart: string;
    DateServiceEnd: string;
    DateToOrderBy: string;
    CustomerReference: string;
    ServiceType: string;
    Price: string;
    Callout: string;
    Routine: string;
    Followup: string;
    Address1: string;
    Address2: string;
    Address3: string;
    Postcode: string;
    OrderNumber: string;
    VisitTypeRef: string;
    CalledInBy: string;
    CardNumber: string;
    VisitTypeDesc: string;
    ServiceLine: string;
    PestsActive?: string;
    DueFrom?: string;
    DueBy?: string;
}

export interface TechnicianTraining {
    TechnicianForename: string;
    TechnicianSurname: string;
    CurrentlyEmployed: string;
    Name: string;
    IssueDate: string;
    CertificateNumber: string;
    ExpiryDate?: string;
    TechnicianID: string;
    TrainingID?: string;
}
export interface LocationList {
    CardNumber: string;
    Location: string;
    LocationDescription: string;
    Name: string;
    Number: string;
    Position: string;
    ConnectedTrapService: string;
    Battery: string;
    Signal: string;
    Temperature: string;
    Status: string;
}

export interface SiteService {
    AnnualContractPrice: string;
    CardNumber: string;
    DateOrderExpiry: string;
    DateInput: string;
    DateInActive: string;
    LocationList: string;
    LocationPlan: string;
    Name: string;
    NameCode: string;
    Photo: string;
    SiteName: string;
    ServiceOrderNumber: string;
    ServiceType: string;
    SiteRef: string;
    StartDate: string;
    TechnicianID: string;
    TechnicianForename: string;
    TechnicianSurname: string;
    technicianTraining: TechnicianTraining[];
    VisitFrequency: string;
    servicesProvidedContractedSpec?: {
        CardNumber: string;
        Deleted: string;
        ServiceType: string;
    }[];
    locationList: LocationList[];
    locationPlan: string[];
}

export interface SiteTopSiteData {
    SiteID: string; // sometimes SiteID is returned, sometimes SiteRef both are the same
    SiteName: string;
    CountOfFindings: number;
}

const sitesService = {
    /** Get individual site */
    getSite: (siteId: string | number) =>
        fetchPlus<ApiResponse<"data", Site>>(`${process.env.NEXT_PUBLIC_EXT_API_URL}/sites/${siteId}`, "GET"),

    /** Update site details */
    updateSite: (
        siteId: string,
        updatedSite: Omit<
            Site,
            | "SiteRef"
            | "SiteLatitude"
            | "SiteLongitude"
            | "LastRoutineVisit"
            | "LastRoutineVisitType"
            | "LastCalloutVisit"
            | "LastCalloutVisitType"
            | "LastFollowupVisit"
            | "LastFollowupVisitType"
            | "LastPestActivity"
            | "LastPestActivityType"
            | "LastMaterialUsed"
            | "LastMaterialUsedType"
        >
    ) => fetchPlus<ApiResponse<"data", Site>>(`${process.env.NEXT_PUBLIC_EXT_API_URL}/sites/${siteId}`, "PATCH", updatedSite),

    /** Get all sites for the current user */
    getSites: () => fetchPlus<ApiResponse<"sites", Site[]>>(`${process.env.NEXT_PUBLIC_EXT_API_URL}/sites`, "GET"),

    /** Get a service */
    getService: (serviceId: string | number) =>
        fetchPlus<ApiResponse<"data", SiteService>>(`${process.env.NEXT_PUBLIC_EXT_API_URL}/services/${serviceId}`, "GET"),

    /** Get site Plans */
    getServiceLocationPlans: (serviceId: string | number) =>
        fetchPlus<ApiResponse<"data", string[]>>(`${process.env.NEXT_PUBLIC_EXT_API_URL}/services/${serviceId}/plans`, "GET"),

    /** Get all services that apply to a site */
    getServices: (siteId: string | number) =>
        fetchPlus<ApiResponse<"data", SiteService[]>>(`${process.env.NEXT_PUBLIC_EXT_API_URL}/sites/${siteId}/services`, "GET"),

    /** Get all visits for a site */
    getVisits: (siteId: string | number, fromDate?: Date | null, toDate?: Date | null) => {
        const fromDateObj = dayjs(fromDate);
        const toDateObj = dayjs(toDate);

        const queryParameters = qs.stringify({
            fromDate: fromDateObj.isValid() ? fromDateObj.format("YYYY-MM-DD") : undefined,
            toDate: toDateObj.isValid() ? toDateObj.format("YYYY-MM-DD") : undefined,
        });

        return fetchPlus<ApiResponse<"data", SiteVisitPreview[]>>(
            `${process.env.NEXT_PUBLIC_EXT_API_URL}/sites/${siteId}/visits${queryParameters ? `?${queryParameters}` : ""}`,
            "GET"
        );
    },

    getOutstandingActions: (siteId: string | number) =>
        fetchPlus<ApiResponse<"data", Action[]>>(`${process.env.NEXT_PUBLIC_EXT_API_URL}/sites/${siteId}/actions`, "GET"),

    getMaterials: (siteId: string | number) =>
        fetchPlus<ApiResponse<"data", Material[]>>(`${process.env.NEXT_PUBLIC_EXT_API_URL}/sites/${siteId}/materials`, "GET"),

    getClosedActions: (siteId: string | number) =>
        fetchPlus<ApiResponse<"data", Action[]>>(`${process.env.NEXT_PUBLIC_EXT_API_URL}/sites/${siteId}/actions?type=CLOSED`, "GET"),

    /** Get top sites that have the most actions */
    getTopSites: () => fetchPlus<ApiResponse<"sites", SiteTopSiteData[]>>(`${process.env.NEXT_PUBLIC_EXT_API_URL}/sites?type=top`, "GET"),

    /** Charts */
    getPestActivityPCMGraphData: (siteId: string) =>
        fetchPlus<ApiResponse<"chartData", ChartData>>(
            `${process.env.NEXT_PUBLIC_INTERNAL_API_URL}/sites/activity/pest-activity-by-month?siteId=${siteId}`,
            "GET"
        ),
    getPestActivityGraphYTDData: (siteId: string) =>
        fetchPlus<ApiResponse<"chartData", ChartData>>(
            `${process.env.NEXT_PUBLIC_INTERNAL_API_URL}/sites/activity/pest-activity-data-ytd?siteId=${siteId}`,
            "GET"
        ),
    getActionsLoggedByOwner: (siteId: string) =>
        fetchPlus<ApiResponse<"chartData", ChartData>>(
            `${process.env.NEXT_PUBLIC_INTERNAL_API_URL}/sites/actions/by-owner-logged?siteId=${siteId}`,
            "GET"
        ),
    getPestActivityDetailYTD: (siteId: string) =>
        fetchPlus<ApiResponse<"detail", ActivityDetail[]>>(
            `${process.env.NEXT_PUBLIC_EXT_API_URL}/sites/${siteId}/activity/details?type=PEST`,
            "GET"
        ),

    /** Selected site Action donuts */
    getAllActions: (siteId: string) =>
        fetchPlus<ApiResponse<"donutData", DoughnutDataset[]>>(
            `${process.env.NEXT_PUBLIC_INTERNAL_API_URL}/sites/actions/by-all-types?siteId=${siteId}`,
            "GET"
        ),

    /** Selected site Visit donuts */
    getVisitsByCallout: (siteId: string) =>
        fetchPlus<ApiResponse<"count", TotalPunctual>>(
            `${process.env.NEXT_PUBLIC_INTERNAL_API_URL}/sites/visits/by-callout?siteId=${siteId}`,
            "GET"
        ),
    getVisitsByFollowUp: (siteId: string) =>
        fetchPlus<ApiResponse<"count", TotalPunctual>>(
            `${process.env.NEXT_PUBLIC_INTERNAL_API_URL}/sites/visits/by-follow-up?siteId=${siteId}`,
            "GET"
        ),
    getVisitsByRoutines: (siteId: string) =>
        fetchPlus<ApiResponse<"count", TotalPunctual>>(
            `${process.env.NEXT_PUBLIC_INTERNAL_API_URL}/sites/visits/by-routines?siteId=${siteId}`,
            "GET"
        ),

    getPestActivityByArea: (siteId: string) =>
        fetchPlus<ApiResponse<"chartData", ChartData>>(
            `${process.env.NEXT_PUBLIC_INTERNAL_API_URL}/sites/activity/pest-activity-data-area?siteId=${siteId}`,
            "GET"
        ),
};

export default sitesService;
